import { stableRecordKey } from '../../lib/rowIdentity';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Check, Users, Shield, ExternalLink, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MandalaCorner } from '../../components/luxury/MandalaCorner';
import { api } from '../../App';
import { toast } from 'sonner';
import { usePageContent, PreviewBanner } from '../../lib/content';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const CONTACT_INFO = [
  { icon: MapPin, label: 'Address', value: '28 Handel Street, Nottingham NG3 1JE', href: 'https://maps.google.com/?q=28+Handel+Street+Nottingham+NG3+1JE' },
  { icon: Mail, label: 'Email', value: 'info@gujaratsamajnottingham.com', href: 'mailto:info@gujaratsamajnottingham.com' },
  { icon: MessageCircle, label: 'WhatsApp', value: '+447502076709', href: 'https://wa.me/447502076709' },
];

const CONTACT_SUBJECTS = ['Feedback', 'Membership Query', 'Others', 'Ticket Issues', 'Update Details'];
const emptyForm = () => ({ name: '', email: '', subject: '', other_subject: '', message: '', request_id: crypto.randomUUID() });
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function LuxuryContact() {
  const { t, c, isPreview } = usePageContent('contact');
  const addr = t('address', '28 Handel Street, Nottingham NG3 1JE');
  const email = t('email', 'info@gujaratsamajnottingham.com');
  const wa = t('whatsapp', '+447502076709');
  const phone = t('phone', '');
  const hours = t('office_hours', '');
  const contactInfo = [
    { icon: MapPin, label: 'Address', value: addr, href: t('address_map_link', '') || `https://maps.google.com/?q=${encodeURIComponent(addr)}` },
    { icon: Mail, label: 'Email', value: email, href: `mailto:${email}` },
    { icon: MessageCircle, label: 'WhatsApp', value: wa, href: `https://wa.me/${String(wa).replace(/[^0-9]/g, '')}` },
    ...(phone && phone !== wa ? [{ icon: Phone, label: 'Phone', value: phone, href: `tel:${String(phone).replace(/[^0-9+]/g, '')}` }] : []),
  ];
  const [form, setForm] = useState(emptyForm);
  const [otherError, setOtherError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [people, setPeople] = useState(null); // { committee: [], trustees: [] }
  useEffect(() => { api.get('/api/committee').then(setPeople).catch(() => setPeople({ committee: [], trustees: [] })); }, []);
  const COMMITTEE_MEMBERS = people?.committee || [];
  const TRUSTEES = people?.trustees || [];

  const changeField = (name, value) => {
    setForm(f => ({ ...f, [name]: value, request_id: crypto.randomUUID() }));
    if (name === 'other_subject') setOtherError('');
  };

  const changeSubject = subject => {
    setForm(f => ({ ...f, subject, other_subject: '', request_id: crypto.randomUUID() }));
    setOtherError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please complete the required fields so we can get back to you');
      return;
    }
    if (form.subject === 'Others' && !form.other_subject.trim()) {
      setOtherError('Please specify what your enquiry is about.');
      document.getElementById('luxury-contact-other-subject')?.focus();
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/contact', { ...form, other_subject: form.subject === 'Others' ? form.other_subject.trim() : null });
      toast.success('Your message has been received. Thank you for contacting us.');
      setSubmitted(true);
      setForm(emptyForm());
      setOtherError('');
    } catch (err) {
      toast.error(err.message || 'Unable to send your message. Your details have been kept so you can retry.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {isPreview && <PreviewBanner page="Contact" />}
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-left" className="opacity-20" />
        <MandalaCorner position="bottom-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="luxury-contact-title">
              {t('hero_title', 'Contact')} <span className="text-[hsl(var(--lux-gold))]">{t('hero_title_accent', 'Us')}</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto mb-8">
              {t('hero_subtitle', 'Get in touch with Gujarat Samaj Nottingham')}
            </p>
            <div className="lux-gold-divider mx-auto max-w-md" />
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {hours && <p className="text-center text-sm text-[hsl(var(--lux-ink)/0.7)] mb-6" data-testid="contact-office-hours">{hours}</p>}
          <div className={`grid sm:grid-cols-3 ${contactInfo.length === 4 ? 'lg:grid-cols-4' : ''} gap-6 mb-8`}>
            {contactInfo.map((info, idx) => (
              <motion.div key={stableRecordKey(info)} {...fadeInUp} transition={{ delay: idx * 0.1 }}>
                <a href={info.href} target={info.href?.startsWith('http') ? '_blank' : undefined} rel={info.href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
                  <Card className="lux-ornate-card h-full hover:shadow-[var(--lux-shadow-2)] transition-all group cursor-pointer">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center group-hover:bg-[hsl(var(--lux-royal))] transition-colors">
                        <info.icon className="w-8 h-8 text-[hsl(var(--lux-royal))] group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="text-lg font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                        {info.label}
                      </h3>
                      <p className="text-sm text-[hsl(var(--lux-gold))]">{info.value}</p>
                      {info.href?.startsWith('http') && (
                        <ExternalLink className="w-4 h-4 text-[hsl(var(--lux-ink)/0.4)] mx-auto mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </CardContent>
                  </Card>
                </a>
              </motion.div>
            ))}
          </div>

          {/* Social Media */}
          <motion.div {...fadeInUp} className="text-center" data-testid="contact-social-links">
            <h3 className="text-xl font-bold text-[hsl(var(--lux-royal))] mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>
              Follow Us
            </h3>
            <div className="flex items-center justify-center gap-4">
              <a href={t('facebook_url', 'https://www.facebook.com/share/1UaeYDTuzR/?mibextid=wwXIfr')} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="contact-facebook-link"
                className="w-12 h-12 rounded-full bg-[hsl(var(--lux-royal-soft))] border border-[hsl(var(--lux-gold)/0.3)] flex items-center justify-center hover:bg-[hsl(var(--lux-royal))] transition-colors group">
                <svg className="w-5 h-5 text-[hsl(var(--lux-royal))] group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href={t('instagram_url', 'https://www.instagram.com/gujaratsamajnotts?igsi=MWtrNzFwc2I5NWRjYg%3D%3D&utm_source=qr')} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="contact-instagram-link"
                className="w-12 h-12 rounded-full bg-[hsl(var(--lux-royal-soft))] border border-[hsl(var(--lux-gold)/0.3)] flex items-center justify-center hover:bg-[hsl(var(--lux-royal))] transition-colors group">
                <svg className="w-5 h-5 text-[hsl(var(--lux-royal))] group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-8 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>
                  {t('form_heading', 'Send Us a Message')}
                </h2>
                
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[hsl(var(--lux-gold-soft))] flex items-center justify-center">
                      <Check className="w-10 h-10 text-[hsl(var(--lux-gold))]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                      Message received!
                    </h3>
                    <p className="text-[hsl(var(--lux-ink)/0.7)] mb-6">
                      {t('success_text', "Thank you for reaching out. We'll respond within 48 hours.")}
                    </p>
                    <Button
                      data-testid="contact-send-another-message"
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="border-[hsl(var(--lux-gold)/0.5)]"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          required
                          value={form.name}
                          onChange={e => changeField('name', e.target.value)}
                          maxLength={120}
                          disabled={loading}
                          placeholder="Your name"
                          data-testid="luxury-contact-name-input"
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          required
                          value={form.email}
                          onChange={e => changeField('email', e.target.value)}
                          maxLength={254}
                          disabled={loading}
                          placeholder="your@email.com"
                          data-testid="luxury-contact-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="luxury-contact-subject">Subject</Label>
                      <Select value={form.subject} onValueChange={changeSubject} disabled={loading}>
                        <SelectTrigger id="luxury-contact-subject" className="min-h-11" data-testid="luxury-contact-subject-input">
                          <SelectValue placeholder="What is this about?" />
                        </SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--lux-ivory))] text-[hsl(var(--lux-ink))]">
                          {CONTACT_SUBJECTS.map(subject => <SelectItem key={subject} value={subject} className="min-h-11 focus:bg-[hsl(var(--lux-royal-soft))] focus:text-[hsl(var(--lux-royal))]" data-testid={`contact-subject-${subject.toLowerCase().replaceAll(' ', '-')}`}>{subject}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {form.subject === 'Others' && <div data-testid="contact-other-subject-field">
                      <Label htmlFor="luxury-contact-other-subject">Please specify *</Label>
                      <Input id="luxury-contact-other-subject" required maxLength={200} disabled={loading} value={form.other_subject} onChange={e => changeField('other_subject', e.target.value)} placeholder="What is your enquiry about?" aria-invalid={!!otherError} aria-describedby={otherError ? 'contact-other-error' : undefined} data-testid="luxury-contact-other-subject-input" />
                      {otherError && <p id="contact-other-error" role="alert" className="mt-2 text-sm text-destructive" data-testid="contact-other-subject-error">{otherError}</p>}
                    </div>}
                    <div>
                      <Label>Message *</Label>
                      <Textarea
                        required
                        value={form.message}
                        onChange={e => changeField('message', e.target.value)}
                        maxLength={10000}
                        disabled={loading}
                        placeholder="Tell us more..."
                        rows={6}
                        data-testid="luxury-contact-message-input"
                      />
                    </div>
                    <p className="text-xs text-[hsl(var(--lux-ink)/0.6)]" data-testid="contact-forwarding-notice">Your enquiry is saved securely and a notification is queued to info@gujaratsamajnottingham.com.</p>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-12 text-lg"
                      data-testid="luxury-contact-submit-button"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Committee Members */}
      {(people === null || COMMITTEE_MEMBERS.length > 0) && (
      <section className="py-16 bg-white" data-testid="committee-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeInUp} className="text-3xl font-bold text-center text-[hsl(var(--lux-royal))] mb-12" style={{ fontFamily: 'Cormorant Garamond' }}>
            Committee Members
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {people === null && [0, 1, 2, 3].map(i => <div key={i} className="h-40 rounded-[var(--lux-radius-card)] bg-[hsl(var(--lux-royal-soft))] animate-pulse" />)}
            {COMMITTEE_MEMBERS.map((member, idx) => (
              <motion.div key={member.person_id || idx} {...fadeInUp} transition={{ delay: idx * 0.05 }} data-testid={`committee-member-${member.person_id}`}>
                <Card className="lux-ornate-card hover:shadow-[var(--lux-shadow-2)] transition-all h-full">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center overflow-hidden">
                      {member.photo_url ? <img src={`${BACKEND}${member.photo_url}`} alt={member.name} className="w-full h-full object-cover" loading="lazy" /> : <Users className="w-7 h-7 text-[hsl(var(--lux-royal))]" />}
                    </div>
                    <h3 className="font-bold text-sm mb-1 text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>
                      {member.name}
                    </h3>
                    <p className="text-xs text-[hsl(var(--lux-ink)/0.7)]">{member.role}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      )}

      {/* Trustees */}
      {TRUSTEES.length > 0 && (
      <section className="py-16 bg-[hsl(var(--lux-parchment))]" data-testid="trustees-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeInUp} className="text-3xl font-bold text-center text-[hsl(var(--lux-royal))] mb-12" style={{ fontFamily: 'Cormorant Garamond' }}>
            Trustees
          </motion.h2>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {TRUSTEES.map((trustee, idx) => (
              <motion.div key={trustee.person_id || idx} {...fadeInUp} transition={{ delay: idx * 0.1 }} data-testid={`trustee-${trustee.person_id}`}>
                <Card className="lux-ornate-card hover:shadow-[var(--lux-shadow-2)] transition-all h-full">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--lux-gold-soft))] flex items-center justify-center overflow-hidden">
                      {trustee.photo_url ? <img src={`${BACKEND}${trustee.photo_url}`} alt={trustee.name} className="w-full h-full object-cover" loading="lazy" /> : <Shield className="w-7 h-7 text-[hsl(var(--lux-bronze))]" />}
                    </div>
                    <h3 className="font-bold text-sm mb-1 text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>
                      {trustee.name}
                    </h3>
                    <p className="text-xs text-[hsl(var(--lux-ink)/0.7)]">{trustee.role}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}
    </div>
  );
}
